import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import MiTiendaTab from "@/components/MiTiendaTab";
import NenisPlaceTab from "@/components/NenisPlaceTab";
import { EnviosTab } from "@/components/EnviosTab";
import { CuponerasTab } from "@/components/CuponerasTab";
import { MiPlanTab } from "@/components/MiPlanTab";
import TrilateralMap from "@/components/TrilateralMap";
import { Plus, Search, ShoppingBag, Package, AlertTriangle, Ban, Clock, CheckCircle2, TrendingUp, Trash2, RefreshCw, MessageCircle, Truck } from "lucide-react";

const TAX_RATE = 0.16;
const REFRESH_MS = 10000;

export default function StoreDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("mi-tienda");
  const [productos, setProductos] = useState([]);
  const [subOrdenes, setSubOrdenes] = useState([]);
  const [ventasPOS, setVentasPOS] = useState([]);
  const [q, setQ] = useState("");
  const [ticket, setTicket] = useState([]); // {id,nombre,precio,cantidad,stock}
  const [pagoMetodo, setPagoMetodo] = useState("Efectivo");
  const [plan, setPlan] = useState(null);

  const loadProductos = () => api.get("/productos", { params: { tienda_id: user.tienda_id } }).then(r => setProductos(r.data.productos));
  const loadSubs = () => api.get("/sub-ordenes/tienda").then(r => setSubOrdenes(r.data.sub_ordenes));
  const loadVentas = () => api.get("/pos/ventas").then(r => setVentasPOS(r.data.ventas));

  useEffect(() => {
    loadProductos(); loadSubs(); loadVentas();
    api.get("/suscripcion/mia").then(r => setPlan(r.data)).catch(() => {});
    const t = setInterval(() => { loadSubs(); loadProductos(); }, REFRESH_MS);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => productos.filter(p =>
    !q || p.nombre.toLowerCase().includes(q.toLowerCase()) || (p.sku || "").toLowerCase().includes(q.toLowerCase())
  ), [productos, q]);

  const addTicket = (p) => {
    if (!p.estado_disponible || p.stock_disponible === 0) return;
    setTicket(prev => {
      const ex = prev.find(t => t.id === p.id);
      if (ex) {
        if (ex.cantidad + 1 > p.stock_disponible) { toast.warning("Stock insuficiente"); return prev; }
        return prev.map(t => t.id === p.id ? { ...t, cantidad: t.cantidad + 1 } : t);
      }
      return [...prev, { id: p.id, nombre: p.nombre, precio: p.precio, cantidad: 1, stock: p.stock_disponible }];
    });
  };
  const updTicket = (id, cantidad) => {
    if (cantidad <= 0) return setTicket(prev => prev.filter(t => t.id !== id));
    setTicket(prev => prev.map(t => t.id === id ? { ...t, cantidad: Math.min(cantidad, t.stock) } : t));
  };
  const rmTicket = (id) => setTicket(prev => prev.filter(t => t.id !== id));

  const subtotalPOS = ticket.reduce((s, t) => s + t.precio * t.cantidad, 0);
  const impuestosPOS = subtotalPOS * TAX_RATE;
  const totalPOS = subtotalPOS + impuestosPOS;

  const cobrar = async () => {
    if (!ticket.length) return;
    try {
      await api.post("/pos/venta", {
        items: ticket.map(t => ({ producto_id: t.id, cantidad: t.cantidad })),
        metodo_pago: pagoMetodo,
      });
      toast.success(`Venta cobrada: $${totalPOS.toFixed(2)}`);
      setTicket([]);
      loadProductos(); loadVentas();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Kanban buckets
  const today = new Date().toDateString();
  const porPreparar = subOrdenes.filter(s => ["Preparando", "Pendiente"].includes(s.estado));
  const listos = subOrdenes.filter(s => ["Empacado", "Listo", "En_Ruta"].includes(s.estado));
  const enRuta = subOrdenes.filter(s => s.estado === "En_Ruta");
  const entregadosHoy = subOrdenes.filter(s => s.estado === "Entregado" && new Date(s.created_at).toDateString() === today);

  const avanzar = async (sub, nuevo) => {
    try {
      await api.patch(`/sub-ordenes/${sub.id}/estado`, { estado: nuevo });
      toast.success(`Sub-orden marcada como ${nuevo}`);
      loadSubs();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline text-zinc-500">Panel del locatario</div>
            <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1">Hola, {user.nombre}</h1>
          </div>
          <NuevoProductoDialog onCreated={loadProductos} productos={productos} />
        </div>

        {plan && !plan.activo && (
          <button type="button" data-testid="plan-banner" onClick={() => setTab("plan")} className="w-full text-left mb-6 bg-[#FF3366] text-white rounded-sm p-4 flex flex-wrap items-center gap-3 hover:bg-[#E62958]">
            <span className="font-display font-black text-lg tracking-tight">💎 {plan.pendiente ? "Tu comprobante está en revisión" : "Activa tu PLAN INTEGRAL ($299 MXN / 30 días)"}</span>
            <span className="text-sm text-white/85 flex-1">{plan.pendiente ? "En cuanto el administrador lo apruebe, tus productos y cupones se publican." : "Tus productos no aparecen en el e-commerce ni puedes crear cupones hasta activar el plan. Toca para ver cómo pagar."}</span>
          </button>
        )}
        {plan && plan.activo && plan.dias_restantes <= 5 && (
          <button type="button" data-testid="plan-banner-renovar" onClick={() => setTab("plan")} className="w-full text-left mb-6 bg-amber-100 text-amber-900 rounded-sm p-3 text-sm">💎 Tu PLAN INTEGRAL vence en {plan.dias_restantes} días. Renueva para no perder visibilidad.</button>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm bg-white border border-zinc-200 p-1 h-11 flex-wrap h-auto">
            <TabsTrigger value="mi-tienda" data-testid="tab-mi-tienda" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">🏪 Mi Tienda</TabsTrigger>
            <TabsTrigger value="pos" data-testid="tab-pos" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">🧾 Punto de Venta</TabsTrigger>
            <TabsTrigger value="pedidos" data-testid="tab-pedidos" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">📦 Pedidos e-Commerce</TabsTrigger>
            <TabsTrigger value="inventario" data-testid="tab-inventario" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">📊 Inventario</TabsTrigger>
            <TabsTrigger value="envios" data-testid="tab-envios" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">🌎 Envíos y Foráneos</TabsTrigger>
            <TabsTrigger value="cuponeras" data-testid="tab-cuponeras" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">🎟️ Cuponeras</TabsTrigger>
            <TabsTrigger value="plan" data-testid="tab-plan" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">💎 Mi Plan</TabsTrigger>
            <TabsTrigger value="nenis" data-testid="tab-nenis" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">💃 NENIS PLACE</TabsTrigger>
          </TabsList>

          {/* Mi Tienda - Setup Guide */}
          <TabsContent value="mi-tienda" className="mt-6">
            <MiTiendaTab tiendaId={user.tienda_id} onGoTo={setTab} />
          </TabsContent>

          {/* POS */}
          <TabsContent value="pos" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
              <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input data-testid="pos-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre o SKU…" className="pl-9 rounded-sm h-11" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
                  {filtered.map(p => {
                    const agotado = !p.estado_disponible || p.stock_disponible === 0;
                    const bajo = !agotado && p.stock_disponible < 3;
                    return (
                      <button
                        key={p.id}
                        data-testid={`pos-product-${p.id}`}
                        onClick={() => addTicket(p)}
                        disabled={agotado}
                        className={`text-left rounded-sm border transition-all overflow-hidden ${
                          agotado ? "border-zinc-200 opacity-60 cursor-not-allowed" : "border-zinc-200 hover:border-zinc-900 hover:-translate-y-0.5"
                        }`}
                      >
                        <div className="aspect-square bg-zinc-100 relative">
                          {p.imagen_url && <img src={p.imagen_url} alt={p.nombre} className="w-full h-full object-cover" />}
                          {agotado && (
                            <div className="absolute inset-0 bg-black/60 grid place-items-center">
                              <span className="flex items-center gap-1 bg-zinc-900 text-white px-2 py-1 rounded-sm text-xs font-bold"><Ban className="w-3 h-3" /> AGOTADO</span>
                            </div>
                          )}
                          {bajo && (
                            <div className="absolute top-2 left-2 bg-amber-400 text-amber-950 text-[10px] font-bold px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Últimas {p.stock_disponible}
                            </div>
                          )}
                        </div>
                        <div className="p-2.5">
                          <div className="font-display font-semibold text-sm line-clamp-1">{p.nombre}</div>
                          <div className="flex justify-between items-center mt-1">
                            <span className="font-display font-black">${p.precio.toFixed(2)}</span>
                            <span className="text-[10px] text-zinc-500">Stock {p.stock_disponible}</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Ticket */}
              <div className="bg-white border border-zinc-200 rounded-sm p-5 flex flex-col h-fit sticky top-24">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="overline text-zinc-500">Ticket de venta</div>
                    <div className="font-display font-bold text-xl tracking-tight mt-1">Mostrador</div>
                  </div>
                  {ticket.length > 0 && (
                    <button onClick={() => setTicket([])} className="text-xs text-zinc-500 hover:text-red-600">Vaciar</button>
                  )}
                </div>

                <div className="mt-4 space-y-2 max-h-[300px] overflow-y-auto">
                  {ticket.length === 0 ? (
                    <div className="text-center py-8 text-zinc-400 text-sm border border-dashed border-zinc-200 rounded-sm">
                      <ShoppingBag className="w-6 h-6 mx-auto mb-2" /> Agrega productos
                    </div>
                  ) : ticket.map(t => (
                    <div key={t.id} className="flex items-center gap-2 p-2 border border-zinc-100 rounded-sm">
                      <div className="flex-1">
                        <div className="text-sm font-medium line-clamp-1">{t.nombre}</div>
                        <div className="text-xs text-zinc-500">${t.precio.toFixed(2)} × {t.cantidad}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updTicket(t.id, t.cantidad - 1)} className="w-7 h-7 rounded-sm bg-zinc-100 hover:bg-zinc-200">−</button>
                        <span className="w-6 text-center text-sm font-medium">{t.cantidad}</span>
                        <button onClick={() => updTicket(t.id, t.cantidad + 1)} className="w-7 h-7 rounded-sm bg-zinc-100 hover:bg-zinc-200">+</button>
                        <button onClick={() => rmTicket(t.id)} className="w-7 h-7 rounded-sm text-zinc-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-zinc-200 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span data-testid="pos-subtotal">${subtotalPOS.toFixed(2)}</span></div>
                  <div className="flex justify-between text-zinc-500"><span>IVA (16%)</span><span>${impuestosPOS.toFixed(2)}</span></div>
                  <div className="flex justify-between font-display font-black text-2xl tracking-tight pt-1">
                    <span>Total</span><span data-testid="pos-total">${totalPOS.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-4">
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Método de pago</Label>
                  <Select value={pagoMetodo} onValueChange={setPagoMetodo}>
                    <SelectTrigger data-testid="pos-metodo-pago" className="mt-1 rounded-sm h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Efectivo">Efectivo</SelectItem>
                      <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                      <SelectItem value="Transferencia">Transferencia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  data-testid="pos-cobrar"
                  disabled={!ticket.length}
                  onClick={cobrar}
                  className="mt-5 h-14 w-full text-lg rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold tracking-tight"
                >
                  Cobrar Venta Física
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Pedidos Kanban */}
          <TabsContent value="pedidos" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <KanbanColumn
                title="Por Preparar"
                subtitle="Pagados, empieza a empacar"
                accent="border-t-4 border-[#FF3366]"
                subs={porPreparar}
                actionLabel="Marcar como Empacado"
                onAction={sub => avanzar(sub, "Empacado")}
                showTimer
                testid="col-preparar"
              />
              <KanbanColumn
                title="Listo / Esperando Repartidor"
                subtitle="El repartidor pasará a recogerlos"
                accent="border-t-4 border-amber-400"
                subs={listos}
                actionLabel="Marcar Listo para Entrega"
                onAction={sub => avanzar(sub, "Listo")}
                hideActionIfStates={["Listo", "En_Ruta"]}
                testid="col-listos"
              />
              <KanbanColumn
                title="Entregados hoy"
                subtitle="Historial rápido"
                accent="border-t-4 border-emerald-500"
                subs={entregadosHoy}
                testid="col-entregados"
              />
            </div>

            {/* Envíos en camino — Fase 10 */}
            {enRuta.length > 0 && (
              <div className="mt-8">
                <div className="overline text-zinc-500 mb-3 flex items-center gap-2">
                  <Truck className="w-3.5 h-3.5" />
                  Envíos en camino ({enRuta.length})
                </div>
                <div className="grid gap-4">
                  {enRuta.map(sub => <EnvioEnCamino key={sub.id} sub={sub} />)}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Inventario */}
          <TabsContent value="inventario" className="mt-6">
            <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium text-zinc-600">Producto</th>
                    <th className="px-4 py-3 font-medium text-zinc-600">SKU</th>
                    <th className="px-4 py-3 font-medium text-zinc-600">Precio</th>
                    <th className="px-4 py-3 font-medium text-zinc-600">Stock</th>
                    <th className="px-4 py-3 font-medium text-zinc-600">Estado</th>
                    <th className="px-4 py-3 font-medium text-zinc-600">Publicación</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {productos.map(p => (
                    <tr key={p.id} data-testid={`inv-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {p.imagen_url && <img src={p.imagen_url} alt="" className="w-9 h-9 object-cover rounded-sm border border-zinc-200" />}
                          <span className="font-medium">{p.nombre}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{p.sku || "—"}</td>
                      <td className="px-4 py-3 font-medium">${p.precio.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={p.stock_disponible === 0 ? "text-red-600 font-medium" : p.stock_disponible < 3 ? "text-amber-600 font-medium" : ""}>
                          {p.stock_disponible}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {p.estado_disponible ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0">Disponible</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 border-0">No disponible</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          data-testid={`toggle-mkt-${p.id}`}
                          onClick={async () => {
                            try { await api.patch(`/productos/${p.id}`, { en_marketplace: p.en_marketplace === false }); loadProductos(); }
                            catch (e) { toast.error(formatApiError(e)); }
                          }}
                          className={`text-[11px] px-2 py-1 rounded-sm border ${p.en_marketplace !== false ? "bg-[#D4FF00] border-[#D4FF00]" : "bg-white border-zinc-300 text-zinc-500"}`}
                        >
                          {p.en_marketplace !== false ? "E-commerce general" : "Solo mi tienda"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <AjustarStockDialog producto={p} onDone={loadProductos} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6">
              <div className="overline text-zinc-500 mb-2">Historial POS</div>
              <div className="bg-white border border-zinc-200 rounded-sm p-4 text-sm">
                {ventasPOS.length === 0 ? (
                  <div className="text-zinc-400 text-center py-6">Sin ventas POS aún</div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {ventasPOS.slice(0, 20).map(v => (
                      <div key={v.id} className="flex justify-between py-2">
                        <div>
                          <div className="font-medium">{v.detalles.map(d => `${d.cantidad}× ${d.nombre}`).join(", ")}</div>
                          <div className="text-xs text-zinc-500">{new Date(v.fecha_hora).toLocaleString()} · {v.metodo_pago}</div>
                        </div>
                        <div className="font-display font-bold">${v.total.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Envíos: Uber + Foráneos */}
          <TabsContent value="envios" className="mt-6">
            <EnviosTab tiendaId={user.tienda_id} subOrdenes={subOrdenes} onRefresh={loadSubs} />
          </TabsContent>

          {/* Cuponeras */}
          <TabsContent value="cuponeras" className="mt-6">
            <CuponerasTab />
          </TabsContent>

          {/* Mi Plan */}
          <TabsContent value="plan" className="mt-6">
            <MiPlanTab />
          </TabsContent>

          {/* NENIS PLACE */}
          <TabsContent value="nenis" className="mt-6">
            <NenisPlaceTab tiendaId={user.tienda_id} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function timeSince(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function KanbanColumn({ title, subtitle, accent, subs, actionLabel, onAction, hideActionIfStates = [], showTimer, testid }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick(x => x + 1), 30000); return () => clearInterval(t); }, []);
  return (
    <div data-testid={testid} className={`bg-white rounded-sm border border-zinc-200 ${accent} p-4 min-h-[400px]`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-lg tracking-tight">{title}</div>
          <div className="text-xs text-zinc-500">{subtitle}</div>
        </div>
        <span className="text-xs bg-zinc-100 rounded-full px-2 py-0.5 font-medium">{subs.length}</span>
      </div>
      <div className="mt-4 space-y-3">
        {subs.length === 0 && <div className="text-center text-zinc-400 text-sm py-8 border border-dashed border-zinc-200 rounded-sm">Sin pedidos</div>}
        {subs.map(s => {
          const canAct = actionLabel && !hideActionIfStates.includes(s.estado);
          return (
            <div key={s.id} data-testid={`sub-${s.id}`} className="border border-zinc-200 rounded-sm p-3 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline text-zinc-500">Pedido</div>
                  <div className="font-mono font-bold text-sm">#{s.id.slice(0, 8).toUpperCase()}</div>
                </div>
                {showTimer && (
                  <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                    <Clock className="w-3 h-3" /> {timeSince(s.created_at)}
                  </div>
                )}
              </div>
              <div className="mt-3 text-xs space-y-1">
                {s.items?.map((it, i) => (
                  <div key={i} className="flex justify-between text-zinc-700">
                    <span>{it.cantidad}× {it.nombre}</span>
                    <span className="text-zinc-400">${it.subtotal.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-100 flex justify-between items-center text-xs">
                <span className="text-zinc-500">Cliente: {s.cliente_nombre}</span>
                <Badge className="bg-zinc-100 text-zinc-700 border-0">{s.estado}</Badge>
              </div>
              {canAct && (
                <Button
                  data-testid={`sub-action-${s.id}`}
                  onClick={() => onAction(s)}
                  className="mt-3 w-full h-9 rounded-sm bg-zinc-900 hover:bg-[#FF3366] text-white text-xs font-medium"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> {actionLabel}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EMPTY_PROD = { nombre: "", descripcion: "", precio: "", sku: "", stock_disponible: "", imagen_url: "", categoria: "General", en_marketplace: true };

function NuevoProductoDialog({ onCreated, productos = [] }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_PROD);
  const enMarketplace = productos.filter(p => p.en_marketplace !== false).length;
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/productos", {
        ...form,
        precio: parseFloat(form.precio),
        stock_disponible: parseInt(form.stock_disponible || 0),
      });
      toast.success("Producto creado");
      setOpen(false); setForm(EMPTY_PROD);
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="btn-nuevo-producto" className="rounded-sm bg-[#FF3366] hover:bg-[#E62958]"><Plus className="w-4 h-4 mr-1" />Nuevo producto</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle>Nuevo producto</DialogTitle></DialogHeader>
        <div className="flex gap-2 text-[11px]">
          <span data-testid="count-tienda" className="bg-zinc-100 px-2 py-1 rounded-sm">Tienda online: <b>{productos.length}/30</b></span>
          <span data-testid="count-marketplace" className="bg-[#D4FF00] px-2 py-1 rounded-sm">E-commerce general: <b>{enMarketplace}/5</b></span>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="new-p-nombre" placeholder="Nombre" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} required />
          <Input data-testid="new-p-desc" placeholder="Descripción" value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="new-p-precio" type="number" step="0.01" placeholder="Precio" value={form.precio} onChange={e => setForm(f => ({ ...f, precio: e.target.value }))} required />
            <Input data-testid="new-p-stock" type="number" placeholder="Stock inicial" value={form.stock_disponible} onChange={e => setForm(f => ({ ...f, stock_disponible: e.target.value }))} required />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="SKU" value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} />
            <Input placeholder="Categoría" value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} />
          </div>
          <Input data-testid="new-p-imagen" placeholder="URL de imagen" value={form.imagen_url} onChange={e => setForm(f => ({ ...f, imagen_url: e.target.value }))} />
          <label className="flex items-start gap-2 text-sm cursor-pointer border border-zinc-200 rounded-sm p-3">
            <input type="checkbox" data-testid="new-p-marketplace" checked={form.en_marketplace} disabled={enMarketplace >= 5 && form.en_marketplace === false} onChange={e => setForm(f => ({ ...f, en_marketplace: e.target.checked }))} className="mt-0.5 accent-[#FF3366]" />
            <span>
              <span className="font-medium">Publicar en el e-commerce general</span>
              <span className="block text-[11px] text-zinc-500">Máx. 5 artículos. Si lo desmarcas, solo aparece en tu tienda online (máx. 30).</span>
            </span>
          </label>
          <DialogFooter>
            <Button type="submit" data-testid="new-p-submit" className="bg-[#FF3366] hover:bg-[#E62958]">Crear</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AjustarStockDialog({ producto, onDone }) {
  const [open, setOpen] = useState(false);
  const [delta, setDelta] = useState(0);
  const [motivo, setMotivo] = useState("compra");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/productos/${producto.id}/ajustar-stock`, { delta: parseInt(delta), motivo });
      toast.success("Stock ajustado");
      setOpen(false); setDelta(0); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`adjust-${producto.id}`} variant="outline" size="sm" className="rounded-sm"><RefreshCw className="w-3.5 h-3.5 mr-1" />Ajustar</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-sm">
        <DialogHeader><DialogTitle>Ajustar stock: {producto.nombre}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-xs text-zinc-500">Stock actual: <span className="font-medium">{producto.stock_disponible}</span></div>
          <div>
            <Label>Cantidad (+ suma / − resta)</Label>
            <Input data-testid="adj-delta" type="number" value={delta} onChange={e => setDelta(e.target.value)} placeholder="Ej: 10 o -3" />
          </div>
          <div>
            <Label>Motivo</Label>
            <Select value={motivo} onValueChange={setMotivo}>
              <SelectTrigger data-testid="adj-motivo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="compra">Compra / Mercancía nueva</SelectItem>
                <SelectItem value="merma">Merma</SelectItem>
                <SelectItem value="auditoria">Auditoría</SelectItem>
                <SelectItem value="devolucion">Devolución</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="adj-submit" className="bg-[#FF3366] hover:bg-[#E62958]">Aplicar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EnvioEnCamino({ sub }) {
  const [tracking, setTracking] = useState(null);
  const [orden, setOrden] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [t, o] = await Promise.all([
          api.get(`/ordenes/${sub.orden_id}/tracking-points`),
          api.get(`/ordenes/${sub.orden_id}`).catch(() => ({ data: null })),
        ]);
        setTracking(t.data);
        setOrden(o.data);
      } catch {}
    };
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [sub.orden_id]);

  const buyer = tracking?.buyer;
  const producto = sub.items?.[0]?.nombre || "tu pedido";
  const total = (orden?.total || sub.total || 0).toFixed(2);
  const nombreCli = buyer?.nombre || "cliente";
  const talla = sub.items?.[0]?.talla || sub.items?.[0]?.variante || "estándar";
  const waMsg = encodeURIComponent(
    `¡Hola ${nombreCli}! Tu pedido de ${producto} en talla ${talla} ya salió de la tienda con nuestro repartidor. Por favor ten listo tu pago de $${total} en efectivo. El repartidor te contactará al llegar.`
  );
  const waUrl = buyer?.telefono ? `https://wa.me/${buyer.telefono.replace(/\D/g, "")}?text=${waMsg}` : null;

  return (
    <div data-testid={`envio-${sub.id}`} className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
      <div className="p-4 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">Orden {sub.orden_id?.slice(0, 6).toUpperCase()}</div>
          <div className="font-display font-bold text-lg tracking-tight">Rumbo a {nombreCli}</div>
        </div>
        <div className="flex items-center gap-2">
          {tracking?.driver?.nombre && (
            <Badge className="bg-blue-100 text-blue-700 border-0" data-testid={`envio-driver-${sub.id}`}>
              Repartidor: {tracking.driver.nombre}
            </Badge>
          )}
          {waUrl && (
            <a href={waUrl} target="_blank" rel="noreferrer">
              <Button data-testid={`btn-wa-cliente-${sub.id}`} size="sm" className="rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white">
                <MessageCircle className="w-4 h-4 mr-1.5" /> Notificar Cliente por WhatsApp
              </Button>
            </a>
          )}
        </div>
      </div>
      <div className="h-56">
        <TrilateralMap
          driver={tracking?.driver}
          stores={tracking?.stores || []}
          buyer={tracking?.buyer}
          height="h-full"
        />
      </div>
    </div>
  );
}
